import { z } from "zod";
import { ClaudeModel, EffortLevel, Lane } from "./enums";

/**
 * Auth-Modus eines Laufs (ADR-016): `subscription` nutzt das Claude-Abo des Owners über
 * `CLAUDE_CODE_OAUTH_TOKEN`, `api_key` einen API-Schlüssel mit Ausgabelimit, `none` braucht keinen
 * Anbieterzugang (Mock). Den Modus legt der Server fest, nie der Node.
 */
export const AUTH_MODES = ["none", "subscription", "api_key"] as const;
export const AuthMode = z.enum(AUTH_MODES);
export type AuthMode = z.infer<typeof AuthMode>;

/** Auth-Modi, die ein Node einrichten kann; `none` kann jeder Node. */
export const ProviderAuthMode = z.enum(["subscription", "api_key"]);
export type ProviderAuthMode = z.infer<typeof ProviderAuthMode>;

/** Art eines erreichten Anbieterlimits. */
export const QuotaLimitKind = z.enum(["session", "weekly", "model", "extra_usage", "unknown"]);
export type QuotaLimitKind = z.infer<typeof QuotaLimitKind>;
/** `allowed`: im Rahmen, `allowed_warning`: Warnschwelle erreicht, `rejected`: Limit erreicht. */
export const QuotaStatus = z.enum(["allowed", "allowed_warning", "rejected"]);
export type QuotaStatus = z.infer<typeof QuotaStatus>;

/** GitHub-Repo in der Form `owner/name`. */
export const RepoFullName = z
  .string()
  .max(140)
  .regex(/^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})\/[A-Za-z0-9._-]{1,100}$/, "Erwartet owner/name");

/** Höchstlänge des Auftragstexts eines Laufs; der Prompt der Planerin muss samt Repo-Karte hineinpassen. */
export const MAX_INSTRUCTION_CHARS = 20000;

/** Unveränderliche Auftragsbeschreibung für genau einen Lauf (docs/05 §2). */
export const RunSpec = z.strictObject({
  runId: z.string(),
  workItemId: z.string(),
  workOrderId: z.string(),
  lane: Lane,
  adapter: z.string(),
  authMode: AuthMode,
  instruction: z.string().max(MAX_INSTRUCTION_CHARS),
  acceptanceCriteria: z.array(z.string()),
  allowedPaths: z.array(z.string()),
  baseCommitSha: z.string().nullable(),
  dataClassification: z.enum(["public", "internal", "restricted"]),
  networkProfile: z.enum(["deny-all", "provider-only", "provider-and-registries"]),
  timeoutSeconds: z.number().int().positive().max(7200),
  maxCostUsd: z.number().nonnegative(),
  maxTurns: z.number().int().positive().max(200),
  /** Eingebaute Werkzeuge, die der Agent nutzen darf; alles andere ist gesperrt. */
  allowedTools: z.array(z.string().min(1).max(60)).max(30),
  attempt: z.number().int().positive(),
  /** `studio`: Klärung oder Vorschlag im Planungsstudio, ohne Repo; Ergebnis als JSON-Datei. */
  purpose: z.enum(["work", "studio"]).optional(),
  /** Modell und Aufwand des Laufs (Agenten-Einstellung oder Wahl im Plan). */
  model: ClaudeModel.optional(),
  effort: EffortLevel.optional(),
  /**
   * Dateien, die der Node vor dem Lauf in den Arbeitsbereich legt (etwa Screenshots aus taskFLOW für den
   * Planvorschlag). Inhalte holt er über `/node/v1/runs/<id>/inputs` und prüft die Prüfsumme.
   */
  inputs: z
    .array(
      z.strictObject({
        path: z.string().regex(/^material\/[A-Za-z0-9][A-Za-z0-9._-]{0,120}$/),
        sha256: z.string().regex(/^[0-9a-f]{64}$/),
        sizeBytes: z.number().int().nonnegative(),
      }),
    )
    .max(20)
    .optional(),
  /** Repo des Projekts (M2-03); der Node klont es mit einem kurzlebigen Lese-Token. */
  repository: z
    .strictObject({ fullName: RepoFullName, branch: z.string().min(1).max(200) })
    .nullable()
    .optional(),
});
export type RunSpec = z.infer<typeof RunSpec>;

const base = { at: z.string() };

/** Normalisierte Laufereignisse, die Adapter liefern (Adapter-Ebene, nicht Domäne). */
export const NormalizedRunEvent = z.discriminatedUnion("type", [
  z.strictObject({ ...base, type: z.literal("run_started") }),
  z.strictObject({ ...base, type: z.literal("progress_hint"), message: z.string().max(500) }),
  z.strictObject({ ...base, type: z.literal("text_excerpt"), text: z.string().max(2000) }),
  z.strictObject({
    ...base,
    type: z.literal("tool_call_requested"),
    tool: z.string().max(100),
    summary: z.string().max(500),
  }),
  z.strictObject({
    ...base,
    type: z.literal("approval_needed"),
    question: z.string().max(500),
    options: z
      .array(z.strictObject({ id: z.string(), title: z.string(), pros: z.array(z.string()), cons: z.array(z.string()) }))
      .min(2)
      .max(3),
  }),
  z.strictObject({ ...base, type: z.literal("checkpoint"), label: z.string().max(200) }),
  z.strictObject({
    ...base,
    type: z.literal("artifact_candidate"),
    name: z.string().max(200),
    mediaType: z.string().max(100),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
  }),
  z.strictObject({
    ...base,
    type: z.literal("usage_reported"),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative().nullable(),
    quotaUnits: z.number().nonnegative().nullable(),
    /** Anteil von `inputTokens`, der aus dem Prompt-Cache gelesen wurde (zählt beim Anbieter deutlich weniger). */
    cacheReadTokens: z.number().int().nonnegative().optional(),
    /** Abo-Läufe: geschätzter API-Gegenwert, zählt nie auf ein Budget (docs/03 §8). */
    apiEquivalentUsd: z.number().nonnegative().nullable().optional(),
  }),
  z.strictObject({ ...base, type: z.literal("warning"), message: z.string().max(500) }),
  /**
   * Limitstand des Abos, wie Claude Code ihn mit `rate_limit_event` meldet. `utilization` (0 bis 1) fehlt,
   * wenn der Anbieter keinen Anteil nennt; szyOS schätzt ihn nie.
   */
  z.strictObject({
    ...base,
    type: z.literal("quota_status"),
    limit: QuotaLimitKind,
    status: QuotaStatus,
    utilization: z.number().min(0).max(1).nullable(),
    resetsAt: z.string().nullable(),
  }),
  /** Ergebnis eines Studio-Laufs: Inhalt der Datei `szyos-output.json` (wird im Büro geprüft). */
  z.strictObject({ ...base, type: z.literal("studio_output"), json: z.string().max(60_000) }),
  /** Sicherheitsereignis: Der Egress-Proxy hat eine Verbindung aus der Sandbox gesperrt (ACC-34). */
  z.strictObject({
    ...base,
    type: z.literal("egress_blocked"),
    host: z.string().min(1).max(253),
    port: z.number().int().positive().max(65535).nullable(),
  }),
  z.strictObject({
    ...base,
    type: z.literal("run_completed"),
    summary: z.string().max(2000),
    filesChanged: z.array(z.string()).max(500),
  }),
  z.strictObject({ ...base, type: z.literal("run_failed"), reason: z.string().max(2000) }),
  z.strictObject({ ...base, type: z.literal("run_cancelled") }),
  /** Anbieterlimit erreicht (z. B. Abo-Kontingent): Lauf endet ohne Wiederholung, Auftrag pausiert. */
  z.strictObject({
    ...base,
    type: z.literal("quota_exhausted"),
    limit: QuotaLimitKind,
    resetsAt: z.string().nullable(),
    message: z.string().max(500),
  }),
]);
export type NormalizedRunEvent = z.infer<typeof NormalizedRunEvent>;
export type NormalizedRunEventType = NormalizedRunEvent["type"];

export const TERMINAL_RUN_EVENTS: ReadonlySet<NormalizedRunEventType> = new Set([
  "run_completed",
  "run_failed",
  "run_cancelled",
  "quota_exhausted",
]);
